// src/components/BottomNavBar.jsx
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { doc, onSnapshot, collection, query, where, addDoc, Timestamp, writeBatch, increment, getDoc } from "firebase/firestore";
import { getDb } from "../services/firebase";
import {
  CalendarDays,
  ClipboardList,
  Banknote,
  Users,
  UserCircle,
  Plus,
} from 'lucide-react';
import QuickAppointmentModal from "./QuickAppointmentModal";
import { toast } from "react-toastify";
import { useReservations } from "../hooks/useReservations";
import QuickMenuTooltip from "./QuickMenuTooltip";

export default function BottomNavBar() {
  const { currentUser } = useAuth();
  const location = useLocation();
  const [profile, setProfile] = useState(null);
  const [pendingServicesCount, setPendingServicesCount] = useState(0);
  const [pendingTransfersCount, setPendingTransfersCount] = useState(0);
  const [showQuickModal, setShowQuickModal] = useState(false);
  const [services, setServices] = useState([]);
  const { reservas: reservations } = useReservations(currentUser?.uid);

  // Cargar perfil del usuario
  useEffect(() => {
    if (!currentUser?.uid) return;

    const profileRef = doc(getDb(), "profiles", currentUser.uid);
    const unsubscribe = onSnapshot(profileRef, (snap) => {
      if (snap.exists()) {
        setProfile(snap.data());
      }
    }, (error) => {
      console.error("Error cargando perfil:", error);
    });

    return () => unsubscribe();
  }, [currentUser]);

  // Cargar servicios del profesional
  useEffect(() => {
    if (!currentUser?.uid) return;
    
    // Los servicios están en la subcolección profiles/{userId}/services
    const servicesRef = collection(getDb(), 'profiles', currentUser.uid, 'services');
    const qServices = query(servicesRef);

    const unsubscribe = onSnapshot(servicesRef, (snapshot) => {
      const servicesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setServices(servicesData);
      setPendingServicesCount(0);
    }, (error) => {
      console.error('Error cargando servicios:', error);
      setPendingServicesCount(0);
    });

    return () => unsubscribe();
  }, [currentUser?.uid]);

  // Cargar contador de transferencias pendientes
  useEffect(() => {
    if (!currentUser?.uid) return;
    
    const qPending = query(
      collection(getDb(), 'appointments'),
      where('professionalId', '==', currentUser.uid),
      where('paymentStatus', 'in', ['receipt_uploaded', 'pending_transfer']),
      where('status', '!=', 'cancelled')
    );

    const unsubscribe = onSnapshot(qPending, (snapshot) => {
      const count = snapshot.docs.filter(doc => {
        const data = doc.data();
        return data.status !== 'cancelled';
      }).length;
      setPendingTransfersCount(count);
    }, (error) => {
      console.error('Error cargando transferencias pendientes:', error);
    });

    return () => unsubscribe();
  }, [currentUser?.uid]);

  // Función para crear una cita desde el modal rápido
  const handleCreateAppointment = async ({ date, time, service, clientData, customFieldAnswers = {} }) => {
    if (!currentUser?.uid || !profile) {
      toast.error("Error: No se pudo obtener tu perfil");
      return;
    }

    const normalizedEmail = clientData.email.toLowerCase().trim();
    const sanitizedPhone = clientData.phone.replace(/[^\d+]/g, "");

    const batch = writeBatch(getDb());

    try {
      // Actualizar o crear cliente
      const clientRef = doc(
        getDb(),
        `profiles/${currentUser.uid}/clients`,
        normalizedEmail
      );
      const clientSnap = await getDoc(clientRef);
      
      if (clientSnap.exists()) {
        batch.update(clientRef, {
          name: clientData.name,
          phone: sanitizedPhone,
          lastAppointmentDate: date,
          totalAppointments: increment(1),
        });
      } else {
        batch.set(clientRef, {
          id: normalizedEmail,
          name: clientData.name,
          email: normalizedEmail,
          phone: sanitizedPhone,
          countryCode: clientData.countryCode,
          notes: "",
          firstAppointmentDate: date,
          lastAppointmentDate: date,
          totalAppointments: 1,
          createdAt: Timestamp.now(),
          professionalId: currentUser.uid,
        });
      }

      // Crear la cita
      const newAppointmentRef = doc(collection(getDb(), "appointments"));
      batch.set(newAppointmentRef, {
        professionalId: currentUser.uid,
        professionalName: profile.businessName || profile.fullName,
        date: date,
        time: time,
        clientName: clientData.name,
        clientEmail: normalizedEmail,
        clientPhone: sanitizedPhone,
        clientCountry: clientData.countryCode,
        createdAt: Timestamp.now(),
        serviceId: service.id,
        serviceName: service.name,
        serviceDuration: service.duration,
        serviceCost: service.serviceCost || service.price || 0,
        paymentAmount: 0,
        servicePrice: 0,
        startAt: Timestamp.fromDate(
          new Date(`${date}T${time.split(" - ")[0]}`)
        ),
        customFieldAnswers: customFieldAnswers,
      });

      await batch.commit();
      toast.success("¡Turno reservado exitosamente!");
    } catch (error) {
      console.error("Error creando cita:", error);
      throw error;
    }
  };

  // Solo mostrar si es profesional
  if (!profile || profile.role !== 'profesional') {
    return null;
  }

  // Verificar si la ruta actual es una ruta profesional
  const isProfessionalRoute = location.pathname.startsWith('/agenda-fechas') ||
    location.pathname.startsWith('/servicios') ||
    location.pathname.startsWith('/historial-pagos') ||
    location.pathname.startsWith('/clientes') ||
    location.pathname.startsWith('/profile') ||
    location.pathname.startsWith('/ver-horarios') ||
    location.pathname.startsWith('/historial') ||
    location.pathname.startsWith('/estadisticas') ||
    location.pathname.startsWith('/fechas-deshabilitadas') ||
    location.pathname.startsWith('/integrar') ||
    location.pathname.startsWith('/turnos-con-otros') ||
    location.pathname.startsWith('/referidos') ||
    location.pathname === '/dashboard';

  if (!isProfessionalRoute) {
    return null;
  }

  return (
    <>
      <QuickAppointmentModal
        isOpen={showQuickModal}
        onClose={() => setShowQuickModal(false)}
        profile={profile}
        professionalId={currentUser?.uid}
        services={services}
        reservations={reservations}
        onCreateAppointment={handleCreateAppointment}
      />
      
      <QuickMenuTooltip />
      
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700 shadow-2xl z-40 safe-area-inset-bottom">
        <div className="relative max-w-screen-sm mx-auto px-2 py-2">
        {/* Grid con 5 columnas iguales para alineación perfecta */}
        <div className="grid grid-cols-5 gap-0 items-end relative">
          {/* 1. Agenda (Home/Dashboard) - Posición 1 */}
          <Link
            to="/agenda-fechas"
            className={`flex flex-col items-center justify-center gap-1 p-2 rounded-xl transition-all active:scale-95 ${
              location.pathname.startsWith('/agenda-fechas') || location.pathname === '/dashboard'
                ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700/50'
            }`}
            title="Mi Agenda"
          >
            <CalendarDays className="h-6 w-6" />
            <span className="text-[10px] font-medium text-center leading-tight">
              Agenda
            </span>
          </Link>

          {/* 2. Servicios - Posición 2 */}
          <Link
            to="/servicios"
            className={`flex flex-col items-center justify-center gap-1 p-2 rounded-xl transition-all active:scale-95 relative ${
              location.pathname.startsWith('/servicios')
                ? 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400'
                : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700/50'
            }`}
            title="Mis Servicios"
          >
            {pendingServicesCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 h-4 w-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center border-2 border-white dark:border-slate-800">
                {pendingServicesCount > 9 ? '9+' : pendingServicesCount}
              </span>
            )}
            <ClipboardList className="h-6 w-6" />
            <span className="text-[10px] font-medium text-center leading-tight">
              Servicios
            </span>
          </Link>

          {/* 3. BOTÓN CENTRAL: Crear Cita - Perfectamente centrado */}
          <div className="flex items-center justify-center relative">
            <button
              onClick={() => setShowQuickModal(true)}
              className="flex flex-col items-center justify-center gap-1 w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-600 hover:from-blue-600 hover:via-blue-700 hover:to-indigo-700 text-white shadow-lg hover:shadow-xl transition-all active:scale-90 -mt-6 relative z-10"
              title="Crear Nueva Cita"
            >
              <Plus className="h-7 w-7 text-white" />
            </button>
          </div>

          {/* 4. Cobros - Posición 4 */}
          <Link
            to="/historial-pagos"
            className={`flex flex-col items-center justify-center gap-1 p-2 rounded-xl transition-all active:scale-95 relative ${
              location.pathname.startsWith('/historial-pagos')
                ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400'
                : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700/50'
            }`}
            title="Mis Cobros"
          >
            {pendingTransfersCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 h-4 w-4 bg-amber-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center border-2 border-white dark:border-slate-800">
                {pendingTransfersCount > 9 ? '9+' : pendingTransfersCount}
              </span>
            )}
            <Banknote className="h-6 w-6" />
            <span className="text-[10px] font-medium text-center leading-tight">
              Cobros
            </span>
          </Link>

          {/* 5. Perfil - Posición 5 */}
          <Link
            to="/profile"
            className={`flex flex-col items-center justify-center gap-1 p-2 rounded-xl transition-all active:scale-95 ${
              location.pathname.startsWith('/profile')
                ? 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300'
                : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700/50'
            }`}
            title="Editar Perfil"
          >
            <UserCircle className="h-6 w-6" />
            <span className="text-[10px] font-medium text-center leading-tight">
              Perfil
            </span>
          </Link>
        </div>
      </div>
    </nav>
    </>
  );
}

